import { HttpClient, HttpEvent, HttpRequest } from '@angular/common/http';
import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { APP_CONFIG, Settings } from 'src/app/config/settings';
import { ProfileNote } from 'src/app/credentialing/working-list/models/profile-note.model';
import { ProfileNoteItemDto } from 'src/app/partner-network/notes/models/dto/profile-note-item';
import { SubcontractorContract } from 'src/app/partner-network/subcontractor/models/subcontractor-contract.model';
import { SubcontractorDocument } from 'src/app/partner-network/subcontractor/models/subcontractor-document.model';
import { SubcontractorGridLookups } from 'src/app/partner-network/subcontractor/models/subcontractor-grid-lookups.model';
import { SubcontractorItem } from 'src/app/partner-network/subcontractor/models/subcontractor-item.model';
import { SubcontractorPerDiemContract } from 'src/app/partner-network/subcontractor/models/subcontractor-perdiem-contract.model';
import { SubcontractorPerDiemMarket } from 'src/app/partner-network/subcontractor/models/subcontractor-perdiem-market.model';
import { SubcontractorPermissions } from 'src/app/partner-network/subcontractor/models/subcontractor-permissions.model';
import { SubcontractorProfession } from 'src/app/partner-network/subcontractor/models/subcontractor-profession.model';
import { SubcontractorProfileRequest } from 'src/app/partner-network/subcontractor/models/subcontractor-profile-request.model';
import { SubcontractorProfile } from 'src/app/partner-network/subcontractor/models/subcontractor-profile.model';
import { SubcontractorTravelContract } from 'src/app/partner-network/subcontractor/models/subcontractor-travel-contract.model';
import { OptionModel } from 'src/app/shared/grid/filters/option.model';
import { HospitalType } from 'src/app/shared/lib/facility-rate/models/hospital-types.enum';
import { FetchArgs, PagingToken } from 'src/app/shared/models/core';
import { Lookup } from 'src/app/shared/models/lookup.model';
import { combine } from 'src/app/shared/utilities/route';
import { ValidationResult } from '../../compliance/api/models/validation-result.model';
import { BatchUpdateAssignments } from './models/batch-update-assignments';
import { EmployeeSearchResult } from './models/employee-search-result';
import { SubcontractorInterimLeadershipContract } from './models/subcontractor-interim-leadership-contract.model';
import { SubcontractorLocumsContract } from './models/subcontractor-locums-contract.model';

@Injectable({
    providedIn: 'root'
})
export class SubcontractorService {
    private readonly settings: Settings;
    private readonly coreApiPath: string;
    private readonly coreApiSearchPath: string;

    constructor(
        @Inject(APP_CONFIG) settings: Settings,
        private readonly http: HttpClient,
        @Inject(LOCALE_ID) private readonly locale: string
    ) {
        this.settings = settings;
        this.coreApiPath = combine(settings.coreAPIUrl, 'ayanova/candidates/subcontractors');
        this.coreApiSearchPath = combine(settings.coreAPIUrl, 'ayanova/partnernetwork/candidates');
    }

    upload(userId: number, data: FormData): Observable<ValidationResult[]> {
        return this.http.post<ValidationResult[]>(`${this.coreApiPath}/${userId}/documents`, data);
    }

    downloadVendorDocument(userId: number, documentId: number): Observable<HttpEvent<Blob>> {
        const url = combine(this.coreApiPath, `${userId}/vendor-documents/${documentId}/file`);
        return this.http.request(
            new HttpRequest('GET', url, null, {
                reportProgress: true,
                responseType: 'blob'
            })
        );
    }

    downloadDocument(userId: number, documentId: number): Observable<HttpEvent<Blob>> {
        const url = combine(this.coreApiPath, `${userId}/documents/${documentId}/file`);
        return this.http.request(
            new HttpRequest('GET', url, null, {
                reportProgress: true,
                responseType: 'blob'
            })
        );
    }

    getVendorDocuments(userId: number, args: any): Observable<PagingToken<SubcontractorDocument[]>> {
        const url = combine(this.coreApiPath, `${userId}/vendor-documents?pageSize=${args.pageSize}&skip=${args.skip}`);
        return this.getPaged<any>(url).pipe(
            map(
                (vendorDocuments: PagingToken<SubcontractorDocument[]>) =>
                    ({
                        data: vendorDocuments.data.map(
                            (vendorDocument) =>
                                ({
                                    id: vendorDocument.id,
                                    name: vendorDocument.name,
                                    type: vendorDocument.type,
                                    size: `${Number.parseFloat(
                                        (Number(vendorDocument.size) / (1024 * 1024)).toString()
                                    ).toFixed(2)} MB`
                                }) as SubcontractorDocument
                        ),
                        total: vendorDocuments.total
                    }) as PagingToken<SubcontractorDocument[]>
            )
        );
    }

    getDocuments(userId: number, args: any): Observable<PagingToken<SubcontractorDocument[]>> {
        const url = combine(this.coreApiPath, `${userId}/documents?pageSize=${args.pageSize}&skip=${args.skip}`);
        return this.getPaged<any>(url).pipe(
            map(
                (documents: PagingToken<SubcontractorDocument[]>) =>
                    ({
                        data: documents.data.map(
                            (document) =>
                                ({
                                    id: document.id,
                                    name: document.name,
                                    type: document.type,
                                    size: `${Number.parseFloat(
                                        (Number(document.size) / (1024 * 1024)).toString()
                                    ).toFixed(2)} MB`
                                }) as SubcontractorDocument
                        ),
                        total: documents.total
                    }) as PagingToken<SubcontractorDocument[]>
            )
        );
    }

    getContracts(userId: number, args: any): Observable<PagingToken<SubcontractorContract[]>> {
        const url = combine(this.coreApiPath, `${userId}/contracts?pageSize=${args.pageSize}&skip=${args.skip}`);
        return this.getPaged<SubcontractorContract>(url);
    }

    getContractsByHospType(
        userId: number,
        args: any,
        hospType: number
    ): Observable<PagingToken<SubcontractorContract[]>> {
        const url = combine(
            this.coreApiPath,
            `${userId}/contracts?pageSize=${args.pageSize}&skip=${args.skip}&filter.type=${hospType}`
        );
        return this.getPaged<SubcontractorContract>(url);
    }

    getPerDiemContracts(userId: number, args: any): Observable<any> {
        const url = combine(
            this.coreApiPath,
            `${userId}/contracts/perdiem?pageSize=${args.pageSize}&skip=${args.skip}`
        );
        return this.getPaged<any>(url);
    }

    getPerDiemStatuses(userId: number): Observable<Lookup[]> {
        const url = combine(this.coreApiPath, `${userId}/statuses/perdiem`);
        return this.http.get<Lookup[]>(url);
    }

    getPerDiemMarkets(userId: number): Observable<SubcontractorPerDiemMarket[]> {
        const url = combine(this.coreApiPath, `${userId}/markets/perdiem`);
        return this.http.get<SubcontractorPerDiemMarket[]>(url);
    }

    getPerDiemProfessions(userId: number): Observable<SubcontractorProfession[]> {
        const url = combine(this.coreApiPath, `${userId}/professions/perdiem`);
        return this.http.get<SubcontractorProfession[]>(url);
    }

    getLocumsProfessions(userId: number): Observable<Lookup[]> {
        const url = combine(this.coreApiPath, `${userId}/professions/locums`);
        return this.http.get<Lookup[]>(url);
    }

    getProfessionsByTypeId(id: number): Observable<Lookup[]> {
        const url = combine(this.coreApiPath, `/professions?id=${id}`);
        return this.http.get<Lookup[]>(url);
    }

    getLocumsFacilities(userId: number, searchText: string): Observable<Lookup[]> {
        const url = combine(
            this.coreApiPath,
            `${userId}/hospitals/locums?hospitalName=${encodeURIComponent(searchText)}`
        );
        return this.http.get<Lookup[]>(url);
    }

    getTravelFacilities(userId: number, searchText: string): Observable<Lookup[]> {
        const url = combine(
            this.coreApiPath,
            `${userId}/hospitals/travel?hospitalName=${encodeURIComponent(searchText)}`
        );
        return this.http.get<Lookup[]>(url);
    }

    getFacilitiesByFacilityType(searchText: string, facilityType: HospitalType): Observable<Lookup[]> {
        const url = combine(
            this.coreApiPath,
            `facilities?name=${encodeURIComponent(searchText)}&facilityType=${encodeURIComponent(facilityType)}`
        );
        return this.http.get<Lookup[]>(url);
    }

    getExpertisesByProfession(userId: number, professionId: number): Observable<Lookup[]> {
        const url = combine(this.coreApiPath, `${userId}/professions/${professionId}/expertises`);
        return this.http.get<Lookup[]>(url);
    }

    addLocumsContract(userId: number, isSubcontractor: number, contract: SubcontractorLocumsContract) {
        const url = combine(this.coreApiPath, `${userId}/contracts/locums`);
        return this.http.post(url, {
            startDate: contract.startDate.toLocaleDateString(this.locale),
            endDate: contract.endDate.toLocaleDateString(this.locale),
            expertiseId: contract.expertiseId,
            facilityId: contract.facilityId,
            professionId: contract.professionId,
            userId,
            isSubcontractor,
            locumsContractType: contract.locumsContractType
        });
    }

    addInterimLeadershipContract(
        userId: number,
        isSubcontractor: number,
        contract: SubcontractorInterimLeadershipContract
    ) {
        const url = combine(this.coreApiPath, `${userId}/contracts/interim-leadership`);
        return this.http.post(url, {
            startDate: contract.startDate.toLocaleDateString(this.locale),
            endDate: contract.endDate.toLocaleDateString(this.locale),
            expertiseId: contract.expertiseId,
            facilityId: contract.facilityId,
            professionId: contract.professionId,
            userId,
            isSubcontractor
        });
    }

    addAccessControlContract(userId: number, contract: SubcontractorTravelContract): Observable<number> {
        const url = combine(this.coreApiPath, `contracts/access-control`);
        return this.http.post<number>(url, {
            startDate: contract.startDate.toLocaleDateString(this.locale),
            endDate: contract.endDate.toLocaleDateString(this.locale),
            expertiseId: contract.expertiseId,
            facilityId: contract.facilityId,
            professionId: contract.professionId,
            contractedUserId: userId
        });
    }

    addTravelContract(userId: number, form: SubcontractorTravelContract) {
        const url = combine(this.coreApiPath, `${userId}/contracts/travel`);
        return this.http.post(url, {
            startDate: form.startDate.toLocaleDateString(this.locale),
            endDate: form.endDate.toLocaleDateString(this.locale),
            expertiseId: form.expertiseId,
            facilityId: form.facilityId,
            professionId: form.professionId,
            userId,
            isSubcontractor: form.isSubcontractor
        });
    }

    updatePerDiemContractStatus({
        userId,
        contractStatus
    }: {
        userId: number;
        contractStatus: SubcontractorPerDiemContract;
    }) {
        const url = combine(this.coreApiPath, `${userId}/contracts/perdiem`);
        return this.http.put(url, contractStatus);
    }

    rejectContract(userId: number, contractId: number, reasonCode: number, cancelDate: Date, explanation: string) {
        const url = combine(this.coreApiPath, `${userId}/contracts/${contractId}/reject`);
        return this.http.post(url, {
            reasonCode,
            cancelDate,
            explanation
        });
    }

    search(args: FetchArgs): Observable<PagingToken<SubcontractorItem[]>> {
        const url = this.coreApiPath;
        // special case
        const prop = 'subcontractor';
        if (args.filter[prop]) {
            this.convertFilter(args.filter, prop);
        }
        return this.postPaged(url, args);
    }

    searchPartnerNetworkCandidates(args: FetchArgs): Observable<PagingToken<SubcontractorItem[]>> {
        const url = this.coreApiSearchPath;
        // special case
        const prop = 'subcontractor';
        if (args.filter[prop]) {
            this.convertFilter(args.filter, prop);
        }
        return this.postPaged(url, args);
    }

    getById(userId: number): Observable<SubcontractorProfile> {
        const url = combine(this.coreApiPath, `${userId}`);
        return this.http.get<SubcontractorProfile>(url);
    }

    addProfileNote(note: ProfileNoteItemDto): Observable<ProfileNoteItemDto> {
        const url = combine(this.coreApiPath, `notes`);
        return this.http.post<ProfileNoteItemDto>(url, note);
    }

    addNote(userId: number, note: ProfileNote) {
        const url = combine(this.coreApiPath, `${userId}/notes`);
        return this.http.post<ProfileNote>(url, note);
    }

    getNotes(userId: number, page: number, offset: number): Observable<ProfileNote[]> {
        const url = combine(this.coreApiPath, `${userId}/notes?page=${page}&offset=${offset}`);
        return this.http.get<ProfileNote[]>(url);
    }

    getNoteTypes(): Observable<Lookup[]> {
        const url = combine(this.settings.coreAPIUrl, 'ayanova/notes/types');
        return this.http.get<Lookup[]>(url);
    }

    getCancelReasons(): Observable<Lookup[]> {
        const url = combine(this.settings.coreAPIUrl, 'ayanova/contracts/cancel-reasons');
        return this.http.get<Lookup[]>(url);
    }

    getProfessions(userId: number): Observable<Lookup[]> {
        const url = combine(this.coreApiPath, `${userId}/professions`);
        return this.http.get<Lookup[]>(url);
    }

    getExpertises(userId: number): Observable<Lookup[]> {
        const url = combine(this.coreApiPath, `${userId}/expertises`);
        return this.http.get<Lookup[]>(url);
    }

    update(userId: number, request: SubcontractorProfileRequest): Observable<unknown> {
        const url = combine(this.coreApiPath, `${userId}`);
        return this.http.put(url, request);
    }

    updateBackgroundHistory(userId: number, backgroundHisotry: OptionModel[]): Observable<unknown> {
        const url = combine(this.coreApiPath, `${userId}/background-history`);
        return this.http.put(url, backgroundHisotry);
    }

    getPerDiemHospitals(userId: number, args: any): Observable<any> {
        const url = combine(
            this.coreApiPath,
            `${userId}/hospitals/perdiem?pageSize=${args.pageSize}&skip=${args.skip}&filter.marketId=${args.filter.marketId}`
        );
        return this.getPaged<any>(url);
    }

    getEmployeesSearch(query: string | null): Observable<EmployeeSearchResult[]> {
        const url = combine(this.coreApiPath, 'search-employees');
        if (query) {
            return this.http.get<EmployeeSearchResult[]>(url, { params: { query } });
        } else {
            return this.http.get<EmployeeSearchResult[]>(url);
        }
    }

    getRecruiters(query: string | null): Observable<Lookup[]> {
        const url = `${this.coreApiPath}/recruiters`;
        if (query) {
            return this.http.get<Lookup[]>(url, { params: { query } });
        } else {
            return this.http.get<Lookup[]>(url);
        }
    }

    getPerDiemRecruiters(query: string | null): Observable<Lookup[]> {
        const url = `${this.coreApiPath}/per-diem-recruiters`;
        if (query) {
            return this.http.get<Lookup[]>(url, { params: { query } });
        } else {
            return this.http.get<Lookup[]>(url);
        }
    }

    getTravelXSpecialists(query: string | null): Observable<Lookup[]> {
        const url = `${this.coreApiPath}/travelxspecialists`;
        if (query) {
            return this.http.get<Lookup[]>(url, { params: { query } });
        } else {
            return this.http.get<Lookup[]>(url);
        }
    }

    getTravelXManagers(query: string | null): Observable<Lookup[]> {
        const url = `${this.coreApiPath}/travelxmanagers`;
        if (query) {
            return this.http.get<Lookup[]>(url, { params: { query } });
        } else {
            return this.http.get<Lookup[]>(url);
        }
    }

    batchUpdateAssignments(batch: BatchUpdateAssignments): Observable<void> {
        const url = combine(this.coreApiPath, 'batch-update-assignments');
        return this.http.patch<void>(url, batch);
    }

    public gridLookup(): Observable<SubcontractorGridLookups> {
        const url = `${this.coreApiPath}/lookups`;
        return this.http.get<SubcontractorGridLookups>(url);
    }

    /**
     * Given the searchText, returns a list of results.
     *
     * @param searchText
     */
    public lookupAgency(searchText: string): Observable<Lookup[]> {
        const filter = '/agency?keyword=';

        const url = combine(this.coreApiPath, filter + encodeURIComponent(searchText));
        return this.http.get<any>(url);
    }

    public lookupExpertise(searchText: string): Observable<Lookup[]> {
        const filter = '/expertise?keyword=';

        const url = combine(this.coreApiPath, filter + encodeURIComponent(searchText));
        return this.http.get<any>(url);
    }

    public getPermissions(userId: number): Observable<SubcontractorPermissions> {
        const url = combine(this.coreApiPath, `${userId}/permissions`);
        return this.http.get<SubcontractorPermissions>(url);
    }

    private convertFilter(filter: CompositeFilterDescriptor, prop) {
        const val = filter[prop];
        if (val.length === 0) {
            delete filter[prop];
        } else if (val.length > 0) {
            filter[prop] = filter[prop].join() === 'true' ? true : false;
        }
    }

    protected postPaged<T>(url: string, fetchArgs: FetchArgs): Observable<PagingToken<T[]>> {
        return this.http.post<PagingToken<T[]>>(url, fetchArgs, { observe: 'response' }).pipe(
            map((response) => {
                return {
                    total: response.body.total,
                    data: response.body.data
                };
            })
        );
    }

    protected getPaged<T>(url: string): Observable<PagingToken<T[]>> {
        return this.http.get<T[]>(url, { observe: 'response' }).pipe(
            map((resp) => {
                const totalCount = resp.body['total'];
                return {
                    total: totalCount,
                    data: resp.body['data']
                };
            })
        );
    }
}
